import { Theme, css, useTheme } from '@emotion/react';

import type { GetServerSidePropsContext, NextPage } from 'next';
import { Trans, useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useStoryQuery } from '@hooks/query/useStoryQuery';
import {
  BROADCAST_CHANNEL,
  useBroadcastChannel,
} from '@hooks/useBroadcastChannel';
import { useModal } from '@hooks/useModal';
import { useScrollPosition } from '@hooks/useScrollPosition';
import useToast from '@hooks/useToast';
import {
  FontSizes,
  Radius,
  Sizes,
  Spacing,
  Typography,
} from 'chatie-design-system';
import {
  AdultFill,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bell,
  BellFill,
  BellStroke,
  Heart,
  HeartFill,
  Icon,
  QuestionCircle,
  VerticalEllipsis,
} from 'chatie-design-system/icon';
import { minutesToMilliseconds } from 'date-fns';

import CharacterDetailCta from '@components/characters/CharacterDetailCta';
import ActionSheet from '@components/common/ActionSheet';
import Adfit from '@components/common/Adfit';
import AdultContentBlocker from '@components/common/AdultContentBlocker';
import Badge, { useBadgePresets } from '@components/common/Badge';
import Button from '@components/common/Button';
import EFinderImage from '@components/common/EFinderImage';
import { LinkItem } from '@components/common/ListItem';
import Seo from '@components/common/Seo';
import { TagList } from '@components/common/Tag';
import StorySnow from '@components/events/StorySnow';
import Border, { BorderType } from '@components/layout/Border';
import FloatingButtonArea from '@components/layout/FloatingButtonArea';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import Pad from '@components/layout/Pad';
import Push, { PushSize } from '@components/layout/Push';
import SafeArea from '@components/layout/SafeArea';
import AppReviewModal from '@components/modal/AppReviewModal';
import HonorApplicantModal from '@components/modal/HonorApplicantModal';
import NotificationModal from '@components/modal/NotificationModal';
import ShareModal from '@components/modal/ShareModal';
import {
  HonorPartnershipStoryInfoModal,
  OriginalStoryInfoModal,
} from '@components/modal/StoryBadgeInfoModal';
import WaitfreeInfoModal from '@components/modal/WaitfreeInfoModal';
import StoryDetailSkeleton from '@components/skeleton/StoryDetailSkeleton';
import DonationButton from '@components/stories/DonationButton';
import EpisodeList from '@components/stories/EpisodeList';
import SimpleStampRanking from '@components/stories/SimpleStampRanking';
import User from '@components/user/User';

import { fetchCharacters } from '@api/chatbot';
import { fetchHonorPartnershipPartners } from '@api/common';
import {
  deleteWatchLaterStory,
  fetchStory,
  postWatchLaterStory,
} from '@api/story';
import {
  CommentObjectType,
  EpisodeReadFrom,
  Platform,
  QUERY_KEYS,
  ReportObjectType,
  SettingType,
  StoryAgeGrade,
  StorySaleType,
  StoryTheme,
  StoryType,
} from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { IStory, IStoryDetail, IWaitfreeStoryDetail } from '@model';
import { linearGradient, media, safeArea } from '@styles/css';
import {
  differenceInSeconds,
  formatDistance,
  formatDuration,
  getYearAndMonth,
  isAfter,
} from '@utils/date';
import {
  useAdultContentGuard,
  useNotification,
  useScreen,
  useUserMarketItemInventories,
} from '@utils/hook';
import { ParsedStorage } from '@utils/localStorage';
import {
  RequiredProperty,
  checkWaitfreeStory,
  ellipsisString,
  getSettingDate,
  isEnv,
  setFlow,
  simplifyNumber,
  stringifyCategory,
  toQueryString,
  trimString,
} from '@utils/misc';
import {
  getRouterQuery,
  useChatPaginationQuery,
  useOptimisticUpdate,
} from '@utils/query';
import {
  combineProps,
  withDehydrate,
  withGetServerSideProps,
} from '@utils/ssr';
import { OfferwallType, useOfferwall, useWebView } from '@utils/webview';

const StoryDonationModal = dynamic(
  () =>
    import('@components/modal/DonationModal').then(
      (mod) => mod.StoryDonationModal
    ),
  { ssr: false }
);
type StoryAction = 'share' | 'report';

const coverImageCSS = (theme: Theme) => css`
  position: relative;
  padding-bottom: 62.5%;
  ${safeArea('top')}

  ${media.tablet} {
    padding-bottom: 0;
    height: 280px;
    max-width: 500px;
    margin: 0 auto;
    border-radius: 0 0 12px 12px;
    overflow: hidden;
  }
  ${media.desktop} {
    height: 320px;
    max-width: 560px;
    border-radius: 0 0 16px 16px;
  }

  > div {
    position: absolute;
    width: 100%;
    padding: ${Spacing.medium}px;
    bottom: 0;
    color: ${theme.true.white};
    ${linearGradient('bottom')}

    ${media.tablet} {
      padding: ${Spacing.xlarge}px;
    }
    ${media.desktop} {
      padding: ${Spacing.xxlarge}px;
    }

    h3 {
      margin-top: ${Spacing.xhsmall}px;
      margin-bottom: ${Spacing.xxsmall}px;
    }

    div {
      button + button {
        margin-left: ${Spacing.medium}px;
      }
    }
  }
`;

const headerLabelCSS = css`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: ${Spacing.xxsmall}px;
  }
`;

const summaryCSS = (theme: Theme) => css`
  position: relative;
  padding-right: ${Spacing.large}px;
  font-size: ${FontSizes.small}px;
  color: ${theme.var.gray600};

  button {
    position: absolute;
    bottom: ${Spacing.xxxsmall}px;
    right: 0;
  }
`;

const expiredCSS = css`
  padding: ${Spacing.small}px ${Spacing.medium}px;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${Spacing.xhsmall}px;
  color: #721c24;
  border-radius: ${Radius.small}px;

  ${media.tablet} {
    padding: ${Spacing.small}px ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding: ${Spacing.small}px ${Spacing.xxlarge}px;
  }

  p {
    ${Typography.body14};
  }
`;

const categoryCSS = css`
  margin-top: ${Spacing.small}px;
`;

const userFollowerCountCSS = (theme: Theme) => css`
  display: block;
  font-size: ${FontSizes.xsmall}px;
  margin-top: ${Spacing.xxsmall}px;
  color: ${theme.var.gray500};
`;

const waitfreeCSS = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    font-size: ${FontSizes.small}px;

    span + span {
      margin-left: ${Spacing.xsmall}px;
      padding-left: ${Spacing.xsmall}px;
      border-left: 1px solid ${theme.var.gray200};
    }

    button {
      margin-left: 0;
    }
  }
`;

const waitfreeProgressCSS = (theme: Theme) => css`
  position: relative;
  background-color: ${theme.var.gray300};
  color: ${theme.true.white};
  width: 80px;
  height: 20px;
  border-radius: ${Radius.small}px;
  overflow: hidden;
  text-align: center;

  > div {
    position: absolute;
    transition: width 0.2s;
    width: 100%;
    height: 100%;
    background-color: ${theme.colors.main500};
  }

  b {
    position: relative;
    z-index: 1;
    font-size: ${FontSizes.xsmall}px;
  }
`;

const induceOfferwallCSS = (theme: Theme) => css`
  padding: ${Spacing.small}px ${Spacing.medium}px;
  color: ${theme.var.black};
  background-color: ${theme.colors.main50};

  ${media.tablet} {
    padding: ${Spacing.small}px ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding: ${Spacing.small}px ${Spacing.xxlarge}px;
  }

  p {
    text-align: center;
    ${Typography.body14}

    span {
      text-decoration: underline;
    }
  }
`;

const honorApplicantCSS = () => (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(
      --service-ultramarine_400,
      linear-gradient(0deg, #5563e1 0%, #5563e1 100%),
      linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0.2) 100%
      ),
      #2e41ef
    );

    padding: 10px 8px 10px 16px;
    ${Typography.body12};
    color: ${theme.var.white};
    line-height: 15.6px;

    h6 {
      ${Typography.body12}
      font-weight:700;
    }

    .enter-button {
      display: flex;
      margin-left: ${Spacing.small}px;
      align-items: center;
      gap: ${Spacing.xxsmall}px;
      ${Typography.body12}
      color:${theme.var.white}
    }
  `;

const honorBannerCSS = () => (theme: Theme) =>
  css`
    display: flex;
    justify-content: center;
    background: ${`linear-gradient(90deg, #04C8D0 0%, #1590D5 95.45%)`};
    padding: ${Spacing.xhsmall}px 0;
    ${Typography.body12};
    color: ${theme.var.white};
    line-height: 12px;
  `;

const donationRankingTextCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.colors.main500};
`;

function isWaitfreeReady(
  story: IWaitfreeStoryDetail,
  now = new Date()
): story is RequiredProperty<IWaitfreeStoryDetail, 'waitfreeReset'> {
  return !!story.waitfreeReset && isAfter(story.waitfreeReset, now);
}

const StoryDetail = () => {
  const { t } = useTranslation();
  const { platform } = useGlobalContext();
  const { web } = useWebView();
  const { showToast } = useToast();
  const router = useRouter();
  const storyId = getRouterQuery(router.query.storyId) as number;
  const queryClient = useQueryClient();
  const optimistic = useOptimisticUpdate();

  const theme = useTheme();
  const { mainFill, honorFill, redFill, purpleFill } = useBadgePresets();

  const { useFetchStory, useWeeklySummary } = useStoryQuery();
  const { data: story } = useFetchStory(storyId, { suspense: true });
  const { data: weeklySummaries } = useWeeklySummary(storyId);

  const adultGuard = useAdultContentGuard(
    story?.ageGrade === StoryAgeGrade.adult
  );

  const queryKey = QUERY_KEYS.STORY.ONE(storyId);
  const characterQueryKey = QUERY_KEYS.CHARACTER.STORY_CHARACTER(
    story?.id as number
  );

  const { openOfferwall } = useOfferwall();

  useBroadcastChannel<string>(BROADCAST_CHANNEL.CHARACTER_UPDATE, () => {
    queryClient.refetchQueries(characterQueryKey);
  });

  const isExpiredStory = story?.licenseStatus === 'inactive';

  const isProduction = isEnv('production');
  const showSnow = story
    ? story?.tags?.findIndex(
        (tag) => tag.id === (isProduction ? 571093 : 419)
      ) >= 0
    : false;

  const { data: userSetting } = useNotification(
    SettingType.story,
    storyId,
    'newEpisodePushStatus'
  );
  const notificationOption =
    userSetting?.setting['newEpisodePushStatus'] ?? 'default';
  useUserMarketItemInventories([story?.user]);

  const { mutate: toggleLikeStory, isLoading: isLikeStoryPending } =
    useMutation((id: number) => {
      return (
        story?.isWatchLater ? deleteWatchLaterStory : postWatchLaterStory
      )(id);
    });

  /**
   * states
   */
  const [now, setNow] = useState(new Date());
  const [isSummaryOpen, setSummaryOpen] = useState(false);
  const [summary1, summary2] = trimString(story?.summary ?? '');

  const query = useQuery('honorPartners', fetchHonorPartnershipPartners, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const { data: honorData } = query;

  const characterQuery = useChatPaginationQuery(
    characterQueryKey,
    ({ pageParam }) =>
      fetchCharacters(
        { story_id: story?.id, order_by: 'stat__message_count' },
        pageParam
      ),
    {
      enabled: !!story?.id,
    }
  );

  const filteredCharacter = useMemo(() => {
    const characters = characterQuery?.data?.pages[0]?.items;

    if (characters && (characters?.length ?? 0) > 0) {
      return characters[0];
    }
    return undefined;
  }, [characterQuery?.data?.pages]);

  /**
   * modal
   */
  const [isShareModalOpen, { open: openShareModal, close: closeShareModal }] =
    useModal('story-share-modal');
  const [
    isHonorPartnershipStoryInfoModalOpen,
    {
      open: openHonorPartnershipStoryInfoModal,
      close: closeHonorPartnershipStoryInfoModal,
    },
  ] = useModal('honor-partnership-story-info-modal');
  const [
    isHonorPartnershipApplicantInfoModalOpen,
    {
      open: openHonorPartnershipApplicantInfoModal,
      close: closeHonorPartnershipApplicantInfoModal,
    },
  ] = useModal('honor-partnership-applicant-info-modal');

  const [
    isOriginalStoryInfoModalOpen,
    { open: openOriginalStoryInfoModal, close: closeOriginalStoryInfoModal },
  ] = useModal('original-story-info-modal');

  const [
    isNotificationModalOpen,
    { open: openNotificationModal, close: closeNotificationModal },
  ] = useModal('story-notification-modal');
  const [
    isWaitfreeInfoModalOpen,
    { open: openWaitfreeInfoModal, close: closeWaitfreeInfoModal },
  ] = useModal('waitfree-info-modal');

  const [
    isDonationModalOpen,
    { open: openDonationModal, close: closeDonationModal },
  ] = useModal('donation-modal');
  const [
    isAppReviewModalOpen,
    { open: openAppReviewModal, close: closeAppReviewModal },
  ] = useModal('app-review-modal');

  const handleAction = useCallback(
    (value: StoryAction) => {
      switch (value) {
        case 'share':
          openShareModal();
          return;
        case 'report':
          router.push(
            `/report${toQueryString({
              objectType: ReportObjectType.episode,
              storyId,
            })}`
          );
          return;
        default:
          return;
      }
    },
    [router]
  );

  const handleGoToStoryCommentPage = useCallback(() => {
    router.push(
      `/comments${toQueryString({
        objectType: CommentObjectType.story,
        objectId: storyId,
        storyId: storyId,
      })}`
    );
  }, [storyId]);

  const handleLikeStory = () => {
    toggleLikeStory(storyId, {
      onSuccess: (data) => {
        optimistic.update<IStory>(queryKey, (item) => ({
          ...item,
          isWatchLater: !item.isWatchLater,
        }));

        if (data) {
          const hideAppReviewUntil =
            ParsedStorage.getItem('hideAppReviewUntil');
          if (!hideAppReviewUntil || isAfter(new Date(), hideAppReviewUntil)) {
            openAppReviewModal();
          }
          try {
            if (platform === Platform.web) {
              showToast({
                text: '보관함 내 좋아요에 추가되었습니다.',
                height: 100,
              });
            }
            web.showToast(t('보관함 내 좋아요에 추가되었습니다.'));
          } catch (err) {
            return;
          }
        } else {
          try {
            if (platform === Platform.web) {
              showToast({
                text: '보관함 내 좋아요에서 삭제되었습니다.',
                height: 100,
              });
            }
            web.showToast(t('보관함 내 좋아요에서 삭제되었습니다.'));
          } catch (err) {
            return;
          }
        }
      },
    });
  };

  const handleContinueReadEpisode = () => {
    if (!story) {
      return;
    }

    if (story.latestRead && story.latestRead.episodeTitle) {
      router.push(
        `/stories/${story.id}/episodes/${
          story.latestRead.episodeId
        }${toQueryString({ readFrom: EpisodeReadFrom.cta })}`
      );
      return;
    }

    if (story.firstEpisode) {
      router.push(`/stories/${story.id}/episodes/${story.firstEpisode?.id}`);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, minutesToMilliseconds(1));

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setFlow({ name: '작품상세' });
  }, []);

  if (!story) {
    return null;
  }

  const isWaitfreeStory = checkWaitfreeStory(story);

  const notificationIconMapper = {
    enable: BellFill,
    disable: BellStroke,
    partial_enable: Bell,
    default: Bell,
  };

  const isNotificationDisabled =
    notificationOption === 'default' || notificationOption === 'disable';

  const honorList = honorData?.map((honorUser) =>
    honorUser.partner.id.toString()
  );

  const current = getSettingDate()?.date ?? new Date();

  const eventStartDate = `${getYearAndMonth(current)}-01T00:00:00`;
  const eventEndDate = `${getYearAndMonth(current)}-25T23:59:59`;

  const isEventStart = isAfter(current, eventStartDate);
  const isEventEnd = isAfter(current, eventEndDate);

  const isHonorAuthor =
    story.user.hasHonorPartnership &&
    honorList?.includes(story.user.id.toString() as unknown as string) &&
    isEventStart &&
    !isEventEnd;

  const isHonorPartnershipApplicant =
    !isHonorAuthor && story.isHonorPartnershipApplicant;

  if (adultGuard.isBlocked) {
    return (
      <AdultContentBlocker
        needsLogin={adultGuard.needsLogin}
        needsCertification={adultGuard.needsCertification}
        isLoading={adultGuard.isLoading}
      >
        <></>
      </AdultContentBlocker>
    );
  }

  return (
    <>
      <Head>
        <meta property="og:title" content={story?.title} />
        <meta
          property="og:image"
          content={story?.coverImage.attachment.url}
        />{' '}
        <meta
          property="og:image:width"
          content={story.coverImage.attachment.width?.toString() ?? '1200'}
        />
        <meta
          property="og:image:height"
          content={story.coverImage.attachment.height?.toString() ?? '600'}
        />
      </Head>
      <Seo title={story.title} />
      <Header
        transparent
        transparentFadeInAt="100px"
        push={false}
        right={(color) => (
          <>
            {!isExpiredStory && (
              <>
                <button
                  className="icon-btn"
                  disabled={isLikeStoryPending}
                  onClick={handleLikeStory}
                >
                  <Icon
                    icon={story.isWatchLater ? HeartFill : Heart}
                    color={story.isWatchLater ? theme.colors.yellow500 : color}
                  />
                </button>
                <button className="icon-btn" onClick={openNotificationModal}>
                  <Icon
                    icon={notificationIconMapper[notificationOption]}
                    color={
                      isNotificationDisabled ? color : theme.colors.yellow500
                    }
                  />
                </button>
                <ActionSheet
                  name="my-story-action"
                  options={[
                    { label: t('공유하기'), value: 'share' },
                    { label: t('신고하기'), value: 'report' },
                  ]}
                  onSelect={handleAction}
                >
                  {(open) => (
                    <button className="icon-btn" onClick={open}>
                      <Icon icon={VerticalEllipsis} color={color} />
                    </button>
                  )}
                </ActionSheet>
              </>
            )}
          </>
        )}
      />

      <div css={coverImageCSS}>
        <EFinderImage image={story.coverImage} />
        <div> {showSnow && <StorySnow />}</div>
        <div>
          <div css={headerLabelCSS}>
            {story.ageGrade === StoryAgeGrade.adult && (
              <Icon icon={AdultFill} color={theme.var.gray600} />
            )}
            {story.hasHonorPartnership && (
              <Badge
                text={t('명예작품')}
                {...honorFill}
                onClick={openHonorPartnershipStoryInfoModal}
              />
            )}
            {story.type === StoryType.original && !isExpiredStory && (
              <Badge
                text={t('오리지널')}
                {...redFill}
                onClick={openOriginalStoryInfoModal}
              />
            )}
            {story.theme === StoryTheme.tapNovel && (
              <Badge text={t('탭소설')} {...purpleFill} />
            )}
            {story.isFinished && <Badge text={t('완결')} {...mainFill} />}
          </div>
          <h3>{story.title}</h3>
          {![9817116, 9817120, 9817119, 9817121].includes(story.id) && (
            <div>
              <button className="text-btn icon-text" type="button">
                <span>
                  {simplifyNumber({ value: story.tapCount })} {t('탭')}
                </span>
              </button>
              {story.isCommentEnabled && !isExpiredStory && (
                <button
                  className="text-btn icon-text"
                  type="button"
                  onClick={handleGoToStoryCommentPage}
                >
                  <span>
                    {simplifyNumber({ value: story.commentCount })} {t('댓글')}
                  </span>

                  <Icon
                    icon={ArrowRight}
                    size={Sizes.small}
                    color={theme.true.white}
                  />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <Pad>
        <LinkItem
          to={`/users/${story.user.id}`}
          left={
            <User {...story.user}>
              <small css={userFollowerCountCSS}>
                {t('{{followerCount, simplifyNumber}} 팔로워', {
                  followerCount: story.user.followerCount,
                })}
              </small>
            </User>
          }
        />
      </Pad>
      <Border />
      {isHonorPartnershipApplicant && (
        <div css={honorApplicantCSS}>
          <div>
            <h6>명예작가 투고작을 보고 계시네요!</h6>
            <p>명예작가에 선정되면, 기다리면 무료로 전환됩니다.</p>
          </div>
          <button
            className="enter-button"
            onClick={openHonorPartnershipApplicantInfoModal}
          >
            <span>{t('자세히 보기')}</span>
            <Icon
              icon={ArrowRight}
              size={Sizes.small}
              color={theme.var.white}
            />
          </button>
        </div>
      )}
      {isHonorAuthor && (
        <div css={honorBannerCSS}>
          👑 명예 작가님의 작품입니다. 창작 활동을 응원해 주세요!
        </div>
      )}
      <Pad>
        <div css={summaryCSS}>
          <p className="multiline">
            {summary1}
            {isSummaryOpen && summary2}
          </p>
          {summary2 && (
            <button
              className="icon-btn"
              onClick={() => setSummaryOpen((prev) => !prev)}
            >
              <Icon
                icon={isSummaryOpen ? ArrowUp : ArrowDown}
                size={Sizes.small}
                color={theme.var.gray500}
              />
            </button>
          )}
        </div>
        {story.category && (
          <h6 css={categoryCSS}>{stringifyCategory(story.category)}</h6>
        )}
        <TagList tags={story.tags || []} scroll />
      </Pad>
      {filteredCharacter && (
        <CharacterDetailCta character={filteredCharacter} />
      )}

      {weeklySummaries && weeklySummaries?.length > 0 && !isExpiredStory && (
        <SimpleStampRanking weeklySummary={weeklySummaries[0]} />
      )}

      {/** 기다리면 무료 작품 대기 시간 노출 영역 */}
      {isWaitfreeStory && !isExpiredStory && (
        <>
          <Border />
          <Pad sizes={{ top: Spacing.small, bottom: Spacing.small }}>
            <div css={waitfreeCSS}>
              <p className="align-middle">
                <span>
                  <Trans t={t} i18nKey="기다리면무료 무료회차 N회차">
                    <b>{{ freeEpisodeCount: story.freeEpisodeCount }}</b>
                    회차까지 무료
                  </Trans>
                </span>

                <span>
                  {t('{{waitfreeDuration}}마다 무료', {
                    waitfreeDuration: formatDuration(story.waitfreeDuration),
                  })}
                </span>

                <button className="icon-btn" onClick={openWaitfreeInfoModal}>
                  <Icon
                    icon={QuestionCircle}
                    size={Sizes.small}
                    color={theme.colors.main300}
                  />
                </button>
              </p>

              <div css={waitfreeProgressCSS}>
                {isWaitfreeReady(story, now) ? (
                  <>
                    <div
                      style={{
                        width: `${
                          100 -
                          (differenceInSeconds(story.waitfreeReset, now) /
                            story.waitfreeDuration) *
                            100
                        }%`,
                      }}
                    />
                    <b>
                      {t('{{waitfreeDuration}} 남음', {
                        waitfreeDuration: formatDistance(
                          story.waitfreeReset,
                          now,
                          { addSuffix: false }
                        ),
                      })}
                    </b>
                  </>
                ) : (
                  <>
                    <div />
                    <b>{t('준비됐어요!')}</b>
                  </>
                )}
              </div>
            </div>
          </Pad>

          <WaitfreeInfoModal
            name="waitfree-info-modal"
            open={isWaitfreeInfoModalOpen}
            waitfreeDuration={story.waitfreeDuration}
            waitfreeBlockCount={story.waitfreeBlockCount}
            onClose={closeWaitfreeInfoModal}
          />
        </>
      )}

      {isExpiredStory && (
        <div css={expiredCSS}>
          <Badge {...redFill} text={'라이센스 종료 안내'}></Badge>
          <p>
            {t('해당 작품의 라이센스가 종료되어 소장한 회차만 볼 수 있어요.')}
          </p>
        </div>
      )}

      <div
        css={induceOfferwallCSS}
        onClick={() => {
          return openOfferwall(OfferwallType.tnk);
        }}
      >
        {
          <p>
            {!isExpiredStory &&
            (story.hasBookedPublic ||
              story.saleType === StorySaleType.standardWaitfree) ? (
              <Trans t={t} i18nKey="오퍼월 유도 문구1">
                기다리지 말고 👉🏻<span>여기서</span> 🎈풍선 받으세요!
              </Trans>
            ) : (
              <Trans t={t} i18nKey="오퍼월 유도 문구2">
                채티 생활의 필수템 🎈<b>풍선</b> 👉🏻<span>여기서</span> 받으세요!
              </Trans>
            )}
          </p>
        }
      </div>

      <Adfit type="story" />

      <Border />
      {!isExpiredStory && (
        <Pad>
          <LinkItem
            to={`/stories/${story.id}/donation-ranking`}
            left={<h6>{t('후원 랭킹')}</h6>}
            right={
              <small css={donationRankingTextCSS}>
                {t('후원을 하면 작가님에게 큰 힘이 됩니다!')}
              </small>
            }
          />
        </Pad>
      )}
      {!isExpiredStory && <Border type={BorderType.space} />}

      {/* <KeyBanner bKey={BannerKey.story} size={BannerSize.promotion} /> */}

      {!isExpiredStory && (
        <Pad>
          <Button
            text={
              story.latestRead && story.latestRead.episodeTitle
                ? t('{{episodeTitle}}부터 이어보기', {
                    episodeTitle: ellipsisString(
                      story.latestRead.episodeTitle,
                      15
                    ),
                  })
                : t('첫화보기')
            }
            type="border"
            onClick={handleContinueReadEpisode}
          />
        </Pad>
      )}

      <Border />

      <EpisodeList story={story} />

      <ShareModal
        name="story-share-modal"
        open={isShareModalOpen}
        storyId={storyId}
        shareType={'story'}
        episodeId={story.firstEpisode?.id}
        url={
          story.firstEpisode
            ? `/stories/${story.id}/episodes/${story.firstEpisode?.id}`
            : undefined
        }
        title={story.title}
        summary={story.summary}
        imageUrl={story.coverImage.attachment.url}
        onClose={closeShareModal}
      />

      <NotificationModal
        name="story-notification-modal"
        open={isNotificationModalOpen}
        objectType={SettingType.story}
        objectId={storyId}
        settingKey="newEpisodePushStatus"
        onClose={closeNotificationModal}
        notificationOption={notificationOption}
      />

      <HonorPartnershipStoryInfoModal
        name="honor-partnership-story-info-modal"
        open={isHonorPartnershipStoryInfoModalOpen}
        onClose={closeHonorPartnershipStoryInfoModal}
      />

      <HonorApplicantModal
        name="honor-partnership-applicant-info-modal"
        open={isHonorPartnershipApplicantInfoModalOpen}
        onClose={closeHonorPartnershipApplicantInfoModal}
      />

      <OriginalStoryInfoModal
        name="original-story-info-modal"
        open={isOriginalStoryInfoModalOpen}
        onClose={closeOriginalStoryInfoModal}
      />

      <StoryDonationModal
        name="donation-modal"
        story={story}
        open={isDonationModalOpen}
        onClose={closeDonationModal}
      />

      <AppReviewModal
        name="app-review-modal"
        open={isAppReviewModalOpen}
        onClose={closeAppReviewModal}
      />

      <Push size={PushSize.floatingButton} />

      {!isExpiredStory && (
        <FloatingButtonArea>
          <DonationButton onClick={openDonationModal} />
        </FloatingButtonArea>
      )}
    </>
  );
};

/**
 * Page
 */
const StoryDetailPage: NextPage = () => {
  const router = useRouter();
  const storyId = getRouterQuery(router.query.storyId) as number;

  useScrollPosition(`storyDetail:${storyId}`);
  useScreen('작품 상세');

  return (
    <Layout>
      <Suspense fallback={<StoryDetailSkeleton />}>
        <SafeArea bottom>
          <StoryDetail />
        </SafeArea>
      </Suspense>
    </Layout>
  );
};

export const getServerSideProps = withGetServerSideProps(
  async (ctx: GetServerSidePropsContext) => {
    const storyId = getRouterQuery(ctx.query.storyId) as number;
    const queryKey = QUERY_KEYS.STORY.ONE(storyId);
    const isProduction = isEnv('production');

    if (
      (isProduction && storyId === 4440002) ||
      (!isProduction && storyId === 624919)
    ) {
      return {
        redirect: {
          permanent: false,
          destination: `/stories/${storyId}/licensed`,
        },
      };
    }

    return await combineProps(
      ctx,
      withDehydrate(async (queryClient) => {
        if (ctx.req.url?.startsWith('/_next/data')) {
          return undefined;
        }

        await queryClient.prefetchQuery(queryKey, () => fetchStory(storyId));
        const story = queryClient.getQueryData(queryKey) as IStoryDetail;

        return story
          ? {
              og: {
                title: story.title,
                image: {
                  url: story.coverImage.attachment.url,
                  width: story.coverImage.attachment.width ?? 1200,
                  height: story.coverImage.attachment.height ?? 600,
                },
              },
            }
          : undefined;
      })
    );
  }
);

const StoryDetailLoading = () => {
  return (
    <Layout>
      <StoryDetailSkeleton />
    </Layout>
  );
};

export default withAuth(StoryDetailPage, {
  isSignInRequired: false,
  LoadingComponent: StoryDetailLoading,
});
